import { useMemo } from 'react';

import { type DynamicFormField } from '@flipdish/api-client-typescript';
import { type Property, CreatePropertyTypeEnum } from '@flipdish/orgmanagement';

import { dublinCoordinates } from '../constants';

interface AddressFormAndValuesState {
  readonly values: Property;
  readonly addressFormData: ReadonlyArray<DynamicFormField>;
}

export interface PropertyFormInitialValues {
  readonly name: string;
  readonly countryCode: string;
  readonly emailAddress: string;
  readonly phoneNumber: string;
  readonly addressFields: Record<string, string>;
  readonly coordinates: {
    readonly latitude: number;
    readonly longitude: number;
  };
  readonly logoImageUrl: string;
  readonly type: CreatePropertyTypeEnum;
}

export interface UsePropertyInitialValuesArgs {
  readonly addressFormAndValues: AddressFormAndValuesState;
  readonly propertyData: Property;
  readonly accountEmail: string;
  readonly orgCountryCode: string;
}

const getDynamicAddressFieldsWithInitialValues = ({
  addressFormAndValues,
  propertyData,
}: {
  readonly addressFormAndValues: AddressFormAndValuesState;
  readonly propertyData: Property;
}): Record<string, string> => {
  const initialValues: Record<string, string> = {};

  for (const dynamicField of addressFormAndValues.addressFormData || []) {
    const identifier = dynamicField?.Identifier;
    if (!identifier) continue;

    initialValues[identifier] =
      addressFormAndValues?.values?.addressFields?.[identifier] ||
      propertyData?.addressFields?.[identifier] ||
      '';
  }

  return initialValues;
};

export const usePropertyInitialValues = (
  args: UsePropertyInitialValuesArgs
): PropertyFormInitialValues => {
  const { addressFormAndValues, propertyData, accountEmail, orgCountryCode } = args;

  return useMemo(() => {
    const dynamicAddressFieldsWithInitialValues = getDynamicAddressFieldsWithInitialValues({
      addressFormAndValues,
      propertyData,
    });

    return {
      name: addressFormAndValues?.values?.name || propertyData?.name || '',
      countryCode:
        addressFormAndValues?.values?.countryCode ||
        propertyData?.countryCode ||
        orgCountryCode ||
        '',
      emailAddress:
        addressFormAndValues?.values?.emailAddress ||
        propertyData?.emailAddress ||
        accountEmail ||
        '',
      phoneNumber: addressFormAndValues?.values?.phoneNumber || propertyData?.phoneNumber || '',
      addressFields: dynamicAddressFieldsWithInitialValues,
      coordinates: addressFormAndValues?.values?.coordinates ||
        propertyData?.coordinates || {
          latitude: dublinCoordinates.latitude,
          longitude: dublinCoordinates.longitude,
        },
      logoImageUrl: addressFormAndValues?.values?.logoImageUrl || propertyData?.logoImageUrl || '',
      type:
        addressFormAndValues?.values?.type || propertyData?.type || CreatePropertyTypeEnum.Store,
    };
  }, [accountEmail, addressFormAndValues, orgCountryCode, propertyData]);
};
